import { Injectable, ComponentRef } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';

interface Tab {
  label: string;
  path: string;
  fullPath: string;
  filterData: any;
  params?: any;
  pageNumber: number;
  pageSize: number;
}

interface Stored {
  handle: DetachedRouteHandle;
  scrolls: { idx: number; top: number; left: number }[];
}

@Injectable({
  providedIn: 'root'
})
export class TabService implements RouteReuseStrategy {
  private storageKey = 'tabs';
  private tabsSubject: BehaviorSubject<Tab[]>;
  private activeTabSubject: BehaviorSubject<number>;

  tabs$: any;
  activeTab$: any;

  // ---- lifecycle streams: subscribe from any child component ----
  private deactivatedSubject = new Subject<void>();
  private activatedSubject = new Subject<void>();
  deactivated$ = this.deactivatedSubject.asObservable();
  activated$ = this.activatedSubject.asObservable();

  // ---- reuse cache ----
  private cache = new Map<string, Stored>();

  private singletonPathPatterns = [
    'receiving-services/asn',
    'receiving-services/putaway',
    'receiving-services/grn',
    'order-services/picklist',
    'order-services/order',
    'order-services/manifest',
    'order-services/edit-order',
    'utility/upload',
    'setting/Configurations',
    'transport-services/dispatch',
    'order-services/load-plan',
    'order-services/loading-without-pick',
  ];

  constructor() {
    const savedTabs = sessionStorage.getItem(this.storageKey);
    const initialTabs = savedTabs ? JSON.parse(savedTabs) : [];
    this.tabsSubject = new BehaviorSubject<Tab[]>(initialTabs);

    const savedActiveTab = sessionStorage.getItem(this.storageKey + '_active');
    const initialActiveTab = savedActiveTab ? Number(savedActiveTab) : 0;
    this.activeTabSubject = new BehaviorSubject<number>(initialActiveTab);

    this.tabs$ = this.tabsSubject.asObservable();
    this.activeTab$ = this.activeTabSubject.asObservable();
  }

  // =====================  TAB LOGIC  =====================

  private saveTabs() {
    sessionStorage.setItem(this.storageKey, JSON.stringify(this.tabsSubject.value));
  }

  private saveActiveTab() {
    sessionStorage.setItem(this.storageKey + '_active', this.activeTabSubject.value.toString());
  }

  get tabs(): Tab[] {
    return this.tabsSubject.value;
  }

  get activeTab(): number {
    return this.activeTabSubject.value;
  }

  addTab(tab: Tab) {
    const pathWithParams = tab.params
      ? `${tab.path}?${new URLSearchParams(tab.params).toString()}`
      : tab.path;

    tab.fullPath = pathWithParams;

    const existingTab = this.tabs.findIndex(t => {
      const matchedPattern = this.singletonPathPatterns.find(
        p => t.path.includes(p) && tab.path.includes(p)
      );
      if (matchedPattern) {
        return true;
      }
      return t.fullPath === pathWithParams;
    });

    if (existingTab === -1) {
      this.tabsSubject.next([...this.tabs, tab]);
      this.setActiveTab(this.tabs.length);
    } else {
      const tabs = [...this.tabs];
      const existing = tabs[existingTab];
      const isNewTarget = existing.fullPath !== pathWithParams;

      if (isNewTarget) {
        // singleton tab redirected to a new id/params: destroy the old
        // cached page so the new target gets a fresh component
        this.destroyCached(this.keyFromUrl(existing.fullPath));

        existing.fullPath = pathWithParams;
        existing.path = tab.path;
        existing.params = tab.params;
        existing.pageNumber = 0;
        existing.pageSize = tab.path.includes('order-services/picklist-assign') ? 50 : 10;
        existing.filterData = tab.filterData ?? [];

        this.tabsSubject.next(tabs);
      }

      this.setActiveTab(existingTab);
    }
    this.saveTabs();
    this.saveActiveTab();
  }

  setActiveTab(index: number) {
    this.activeTabSubject.next(index);
    this.saveActiveTab();
  }

  updateTab(index: number, tab: Tab) {
    const tabs = [...this.tabs];
    if (index === tabs.length) {
      index = index - 1;
    }
    if (tabs.length > 0) {
      tabs[index] = tab;
      this.tabsSubject.next(tabs);
      this.saveTabs();
    }
  }

  updatePagination(index: number, pageNumber: number, pageSize: number) {
    const tabs = [...this.tabs];
    if (index === tabs.length) {
      index = index - 1;
    }
    if (tabs[index]) {
      tabs[index].pageNumber = pageNumber;
      tabs[index].pageSize = pageSize;
      this.tabsSubject.next(tabs);
      this.saveTabs();
    }
  }

  closeTab(index: number) {
    const tabs = [...this.tabs];
    const [removed] = tabs.splice(index, 1);
    this.tabsSubject.next(tabs);
    this.saveTabs();

    if (removed) {
      this.destroyCached(this.keyFromUrl(removed.fullPath || removed.path));
    }

    if (this.activeTab === index) {
      if (tabs.length > 0) {
        this.setActiveTab(index === 0 ? 0 : index - 1);
      } else {
        this.setActiveTab(0);
      }
    } else if (this.activeTab > index) {
      this.setActiveTab(this.activeTab - 1);
    }
    this.saveActiveTab();
  }

  closeAllTab() {
    this.tabsSubject.next([]);
    this.saveTabs();
    this.clearAll();
  }

  updateFilterData(index: number, filterData: any) {
    const tabs = [...this.tabs];
    if (index === tabs.length) {
      index = index - 1;
    }
    if (tabs.length > 0) {
      tabs[index].filterData = filterData;
      this.tabsSubject.next(tabs);
      this.saveTabs();
    }
  }

  transformFiltersArrayToObject(filtersArray: any[]): any {
    return filtersArray.reduce((acc, curr) => {
      if (curr.value || curr.value == '0') {
        acc[curr.key] = curr.value;
      }
      if (curr.dateTimeValue) {
        acc[curr.key] = curr.dateTimeValue;
      }
      return acc;
    }, {});
  }

  getTabData(currentPath: string): { pageNumber?: number; pageSize?: number; } | undefined {
    const existingTabIndex = this.tabs.findIndex(t => t.fullPath.includes(currentPath) || t.path.includes(currentPath));

    if (existingTabIndex !== -1) {
      const tab = this.tabs[existingTabIndex];
      return {
        pageNumber: tab.pageNumber,
        pageSize: tab.pageSize
      };
    }

    return { pageNumber: 0, pageSize: 10 };
  }

  // =====================  ROUTE REUSE  =====================

  private makeKey(path: string, query: { [k: string]: any }): string {
    const clean = path.replace(/^\//, '');
    const qs = Object.keys(query).sort().map(k => `${k}=${query[k]}`).join('&');
    return qs ? `${clean}?${qs}` : clean;
  }

  private keyFromUrl(url: string): string {
    const [path, qs] = url.split('?');
    const query: any = {};
    new URLSearchParams(qs || '').forEach((v, k) => (query[k] = v));
    return this.makeKey(path, query);
  }

  private keyFromRoute(route: ActivatedRouteSnapshot): string {
    const path = route.pathFromRoot
      .map(s => s.url.map(u => u.path).join('/'))
      .filter(Boolean)
      .join('/');
    return this.makeKey(path, route.queryParams);
  }

  private isTabRoute(route: ActivatedRouteSnapshot): boolean {
    return !!route.routeConfig && !!route.component && !route.firstChild;
  }

  private hasOpenTab(key: string): boolean {
    return this.tabs.some(t => this.keyFromUrl(t.fullPath || t.path) === key);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.isTabRoute(route) && this.hasOpenTab(this.keyFromRoute(route));
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!handle) return;
    this.cache.set(this.keyFromRoute(route), { handle, scrolls: this.captureScroll(handle) });

    const ref = this.ref(handle);
    ref?.instance?.onTabDeactivated?.();
    this.deactivatedSubject.next();
    ref?.changeDetectorRef.detectChanges();
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.isTabRoute(route) && this.cache.has(this.keyFromRoute(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isTabRoute(route)) return null;
    const stored = this.cache.get(this.keyFromRoute(route));
    if (!stored) return null;
    setTimeout(() => {
      this.restoreScroll(stored);
      const ref = this.ref(stored.handle);
      ref?.instance?.onTabActivated?.();
      this.activatedSubject.next();
      ref?.changeDetectorRef.detectChanges();
    }, 0);
    return stored.handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig !== curr.routeConfig) return false;
    if (this.isTabRoute(future)) return this.keyFromRoute(future) === this.keyFromRoute(curr);
    return true;
  }

  private destroyCached(key: string) {
    const stored = this.cache.get(key);
    if (stored) {
      this.ref(stored.handle)?.destroy();
      this.cache.delete(key);
    }
  }

  private clearAll() {
    [...this.cache.keys()].forEach(k => this.destroyCached(k));
  }

  private ref(handle: DetachedRouteHandle): ComponentRef<any> | undefined {
    return (handle as any)?.componentRef;
  }

  private captureScroll(handle: DetachedRouteHandle) {
    const res: Stored['scrolls'] = [];
    this.ref(handle)?.location?.nativeElement
      ?.querySelectorAll('*')
      .forEach((n: HTMLElement, idx: number) => {
        if (n.scrollTop || n.scrollLeft) res.push({ idx, top: n.scrollTop, left: n.scrollLeft });
      });
    return res;
  }

  private restoreScroll(stored: Stored) {
    const el: HTMLElement | undefined = this.ref(stored.handle)?.location?.nativeElement;
    if (!el) return;
    const nodes = el.querySelectorAll<HTMLElement>('*');
    stored.scrolls.forEach(s => {
      const n = nodes[s.idx];
      if (n) { n.scrollTop = s.top; n.scrollLeft = s.left; }
    });
  }
}